import { RouteResult } from "@gymflow/api";
import { toLowerCaseExceptFirstChar } from "@gymflow/helpers";
import { ClubBean, StaffUserRole, UserStaffBean } from "@gymflow/types";

import { INITIAL_VALUES_STAFF_FORM, roleOptions } from "../constants";
import { RoleOption, StaffFormValues } from "../types";

type GetInitialValuesParams = {
  existingValues?: StaffFormValues;
  club?: ClubBean | null;
  clubRoutes?: RouteResult[];
};

type ClubOption = {
  id: string;
  label: string;
  value: { id: number; name: string };
};

export const getDefaultClub = (
  club?: ClubBean | null,
  clubRoutes?: RouteResult[],
) => {
  if (!club?.id || !clubRoutes?.[0]) return undefined;

  return {
    id: String(club.id),
    label: clubRoutes[0].displayName,
    value: {
      id: club.id,
      name: clubRoutes[0].displayName,
    },
  };
};

const mapRoleForForm = (
  role: StaffUserRole | StaffFormValues["role"],
  isEditing = false,
): RoleOption | null => {
  if (!role) return null;

  if (isEditing) {
    // role is StaffUserRole string
    const roleStr = role as StaffUserRole;
    const formatted = toLowerCaseExceptFirstChar(roleStr);
    return {
      value: roleStr,
      label: formatted,
      id: formatted,
    };
  }

  // role is StaffFormValues["role"] object
  const roleObj = role as StaffFormValues["role"];
  if (roleObj && "value" in roleObj && roleObj.value) {
    return (
      roleOptions.find((r) => r.value === roleObj.value) ?? {
        value: roleObj.value,
        label: roleObj.label ?? toLowerCaseExceptFirstChar(roleObj.value),
        id: String(roleObj.id ?? roleObj.value),
      }
    );
  }
  return null;
};

const mapClubAccessibleList = (
  clubs: number[] | StaffFormValues["clubAccessibleList"],
  routes?: RouteResult[],
  defaultClub?: ClubOption,
  isEditing = false,
): ClubOption[] => {
  if (!clubs) {
    if (defaultClub) {
      return [
        {
          ...defaultClub,
          id: String(defaultClub.value.id),
          value: { ...defaultClub.value, id: Number(defaultClub.value.id) },
        },
      ];
    }
    return [];
  }

  if (isEditing) {
    // clubs is number[]
    return (clubs as number[]).map((id) => {
      const name = routes?.find((r) => r.clubId === id)?.displayName || "";
      return {
        id: String(id),
        label: name,
        value: { id: Number(id), name },
      };
    });
  }

  // Non-editing, clubs is StaffFormValues["clubAccessibleList"]
  return (
    (clubs as StaffFormValues["clubAccessibleList"])?.map((club) => {
      if ("value" in club && club.value?.id != null && club.label) {
        return {
          ...club,
          id: String(club.value.id),
          value: { ...club.value, id: Number(club.value.id) },
        };
      }

      return {
        id: String(club.value.id),
        label: club.value.name,
        value: { id: Number(club.value.id), name: club.value.name },
      };
    }) || []
  );
};

const getStaffFormInitialValues = ({
  existingValues,
  club,
  clubRoutes,
}: GetInitialValuesParams): StaffFormValues => {
  const defaultClub = clubRoutes?.[0]?.displayName
    ? {
        id: String(club?.id ?? 0),
        value: { id: club?.id ?? 0, name: clubRoutes[0].displayName },
        label: clubRoutes[0].displayName,
      }
    : undefined;

  if (existingValues) {
    return {
      ...INITIAL_VALUES_STAFF_FORM,
      ...existingValues,
      role: mapRoleForForm(existingValues.role),
      picture: existingValues.picture ? existingValues.picture : null,
      clubAccessibleList: mapClubAccessibleList(
        existingValues.clubAccessibleList,
        clubRoutes,
        defaultClub,
      ),
    };
  }

  return {
    ...INITIAL_VALUES_STAFF_FORM,
    role: null,
    clubAccessibleList: defaultClub ? [defaultClub] : [],
  };
};

const mapEditingToFormValues = (
  editing: UserStaffBean,
  routesData?: RouteResult[],
): StaffFormValues => {
  return {
    ...editing,
    role: mapRoleForForm(editing.role, true),
    picture: editing.picture ? editing.picture : null,
    clubAccessibleList: mapClubAccessibleList(
      editing.accessibleClubIds,
      routesData,
      undefined,
      true,
    ),
  };
};

const normalizePicture = (
  picture?: string | { blob: string; name?: string } | null,
): { blob: string; name: string } | null => {
  if (!picture) return null;

  if (typeof picture === "string") {
    return { blob: picture, name: "avatar.png" };
  }

  if ("blob" in picture && picture.blob) {
    return { blob: picture.blob, name: picture.name || "avatar.png" };
  }

  return null;
};

export { getStaffFormInitialValues, mapEditingToFormValues, normalizePicture };
